/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  MedicalServices as ConsultationIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useMemo, useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import { abha, consultations, type ConsultationPushBody, type PatientProfile } from '@/api/clinical';
import { me } from '@/api/tenant';
import { useAuth } from '@/auth/AuthContext';
import { DataTable } from '@/components/DataTable';
import { Pagination } from '@/components/Pagination';
import { useToast } from '@/components/Toaster';
import { PageShell } from '@/layout/PageShell';
import { redactMiddle, relativeTime } from '@/utils/format';
import { M2ConsultationForm } from './integration-testing/M2ConsultationForm';

const PAGE_SIZE = 25;

function normalizeGender(g?: string): string {
  if (!g) return 'unknown';
  const u = g.toUpperCase();
  if (u === 'M') return 'male';
  if (u === 'F') return 'female';
  if (u === 'O') return 'other';
  return g.toLowerCase();
}

export function ConsultationsPage() {
  const { claims } = useAuth();
  const tenantId = claims?.tenantId ?? '';
  const toast = useToast();
  const { data: tenantSelf } = useSWR('me-tenant', () => me.tenant());

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState<PatientProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const swrKey = tenantId ? (['patients-cons', tenantId, search, page] as const) : null;
  const { data, error, isLoading } = useSWR(
    swrKey,
    () => abha.listPatients({ tenantId, page, size: PAGE_SIZE, user: search || undefined }),
  );

  const rawPatients = data?.data ?? [];
  const patients: PatientProfile[] = rawPatients.map((p) => ({
    ...p,
    abhaNumber: p.abhaNumber ?? (p as any).abdmHealthId,
    abhaAddress: Array.isArray(p.abhaAddress) ? (p.abhaAddress as string[])[0] : p.abhaAddress,
    name: p.name ?? (p as any).person?.name,
  }));
  const totalCount = data?.totalCount ?? patients.length;
  const pageEnvelope = {
    content: patients,
    totalElements: totalCount,
    totalPages: Math.max(Math.ceil(totalCount / PAGE_SIZE), 1),
    number: page,
    size: PAGE_SIZE,
    first: page === 0,
    last: (page + 1) * PAGE_SIZE >= totalCount,
    numberOfElements: patients.length,
  };

  const refresh = () => globalMutate((k) => Array.isArray(k) && k[0] === 'patients-cons');

  const handleSubmit = async (body: ConsultationPushBody) => {
    setSubmitting(true);
    try {
      await consultations.push(body);
      toast.success(
        'Consultation pushed to ABDM',
        `${body.fhirBundleType} · ${body.consultationNumber}`,
      );
      setSelectedPatient(null);
    } catch (e) {
      toast.error('Push failed', (e as Error).message ?? 'Unknown error');
    } finally {
      setSubmitting(false);
    }
  };

  const ctx = useMemo(() => {
    if (!selectedPatient) return null;
    const abhaNumber = ((selectedPatient.abhaNumber ?? (selectedPatient as any).abdmHealthId) ?? '')
      .replace(/-/g, '');
    const abhaAddress =
      Array.isArray(selectedPatient.abhaAddress)
        ? (selectedPatient.abhaAddress as string[])[0] ?? ''
        : selectedPatient.abhaAddress ?? '';
    return {
      tenantId,
      memberId: (selectedPatient as any).uhidNumber ?? (selectedPatient as any).memberNumber ?? selectedPatient.id ?? '',
      abhaAddress,
      abhaNumber,
      patientName: selectedPatient.name ?? (selectedPatient as any).person?.name ?? '',
      patientGender: normalizeGender(selectedPatient.gender),
      patientBirthDate:
        (selectedPatient as any).dob ??
        (selectedPatient.yearOfBirth ? `${selectedPatient.yearOfBirth}-01-01` : ''),
      facilityName: tenantSelf?.organizationName ?? '',
      facilityId: (tenantSelf as any)?.hfrId ?? tenantId,
      practitionerName: String((claims as any)?.name ?? (claims as any)?.username ?? ''),
      practitionerId: String((claims as any)?.username ?? ''),
    };
  }, [selectedPatient, tenantId, tenantSelf, claims]);

  return (
    <PageShell
      kicker="TENANT · M2 · CONSULTATIONS"
      title="Consultations"
      subtitle="Push FHIR R4 health records to the patient's PHR app via ABDM HIP Init. Select a patient then choose a record type."
      width="wide"
      actions={
        <IconButton onClick={refresh} aria-label="refresh">
          <RefreshIcon fontSize="small" />
        </IconButton>
      }
    >
      <TextField
        size="small"
        label="Search patients"
        placeholder="ABHA number, name, mobile…"
        value={search}
        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        sx={{ mb: 2, minWidth: 320 }}
      />

      <DataTable<PatientProfile>
        rows={patients}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        empty={{
          icon: ConsultationIcon,
          title: 'No patients yet.',
          description: 'Register patients via the ABHA page first, then push consultations here.',
        }}
        columns={[
          {
            header: 'Name',
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="body2" fontWeight={500}>
                  {r.name ?? (r as any).person?.name ?? '—'}
                </Typography>
                {(r as any).uhidNumber && (
                  <Typography variant="caption" color="text.tertiary">
                    UHID {(r as any).uhidNumber}
                  </Typography>
                )}
              </Stack>
            ),
          },
          {
            header: 'ABHA',
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {r.abhaNumber ? redactMiddle(r.abhaNumber, 4, 4) : '—'}
                </Typography>
                {r.abhaAddress && (
                  <Typography variant="caption" color="text.tertiary">
                    {r.abhaAddress}
                  </Typography>
                )}
              </Stack>
            ),
            width: 200,
          },
          { header: 'Mobile', cell: (r) => r.mobile ?? '—', width: 140 },
          { header: 'Gender', cell: (r) => r.gender ?? '—', width: 100 },
          {
            header: 'DOB',
            cell: (r) => {
              const dob = (r as any).dob as string | undefined;
              const yob = (r as any).yearOfBirth;
              if (dob && dob.length >= 10 && !dob.endsWith('-01-01')) {
                const [y, m, d] = dob.split('-');
                return <Typography variant="caption">{d}/{m}/{y}</Typography>;
              }
              if (yob) return <Typography variant="caption" color="text.secondary">{yob}</Typography>;
              return '—';
            },
            width: 110,
          },
          {
            header: 'Updated',
            cell: (r) => (
              <Typography variant="caption" color="text.secondary">
                {relativeTime((r as any).updatedDate)}
              </Typography>
            ),
            width: 140,
          },
          {
            header: '',
            cell: (r) => (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<AddIcon fontSize="small" />}
                onClick={(e) => { e.stopPropagation(); setSelectedPatient(r); }}
                disabled={!r.abhaAddress}
              >
                Add Consultation
              </Button>
            ),
            width: 190,
          },
        ]}
      />
      <Pagination page={pageEnvelope} onChange={setPage} />

      {ctx && (
        <ConsultationDialog
          ctx={ctx}
          patientName={ctx.patientName}
          abhaAddress={ctx.abhaAddress}
          abhaNumber={ctx.abhaNumber}
          gender={ctx.patientGender}
          birthDate={ctx.patientBirthDate}
          onSubmit={handleSubmit}
          submitting={submitting}
          onClose={() => { if (!submitting) setSelectedPatient(null); }}
        />
      )}
    </PageShell>
  );
}

// ─── Consultation Dialog ──────────────────────────────────────────────────────

interface ConsultationDialogProps {
  ctx: Parameters<typeof M2ConsultationForm>[0]['context'];
  patientName: string;
  abhaAddress: string;
  abhaNumber: string;
  gender: string;
  birthDate: string;
  onSubmit: (body: ConsultationPushBody) => Promise<void>;
  submitting: boolean;
  onClose: () => void;
}

function ConsultationDialog({
  ctx,
  patientName,
  abhaAddress,
  abhaNumber,
  gender,
  birthDate,
  onSubmit,
  submitting,
  onClose,
}: ConsultationDialogProps) {
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="h2" sx={{ fontSize: 18, fontWeight: 700, mb: 1 }}>
            Add Consultation Record
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={patientName || 'Patient'}
              color="primary"
              variant="filled"
              sx={{ fontWeight: 600 }}
            />
            {abhaAddress && (
              <Chip
                size="small"
                label={abhaAddress}
                variant="outlined"
                sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}
              />
            )}
            {abhaNumber && (
              <Chip
                size="small"
                label={redactMiddle(abhaNumber, 4, 4)}
                variant="outlined"
                sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}
              />
            )}
            {gender && gender !== 'unknown' && (
              <Chip size="small" label={gender} variant="outlined" />
            )}
            {birthDate && (
              <Chip size="small" label={birthDate} variant="outlined" />
            )}
          </Stack>
        </Box>
        <IconButton onClick={onClose} disabled={submitting} aria-label="close" sx={{ mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, overflow: 'auto', pt: 2 }}>
        <M2ConsultationForm context={ctx} onSubmit={onSubmit} submitting={submitting} />
      </DialogContent>
    </Dialog>
  );
}
